import React, { useEffect, useState } from 'react';
import { ChannelBadge } from './inboxIcons';
import InboxAppearance from './InboxAppearance';
import { AgentSwitch, FactsPanel } from './AgentPage';
import SupportSettings from '../support/SupportSettings';
import AutoReplySettings from './AutoReplySettings';
import ProblemsPage from './ProblemsPage';

const SECTIONS = [
  ['appearance', 'Appearance'],
  ['team', 'Team'],
  ['channels', 'Channels'],
  ['agent', 'AI agent'],
  ['email', 'Email and cart recovery'],
  ['hours', 'Working hours and auto-replies'],
  ['problems', 'Problems'],
];
// Support managers (not admins) only manage working hours, holidays and auto-replies
const MANAGER_SECTIONS = new Set(['hours', 'problems']); // WhatsApp chat flows moved to the WhatsApp page

const initials = (n) => String(n || '?').split(/[\s@.]+/).filter(Boolean).map((p) => p[0]).join('').slice(0, 2).toUpperCase();

// Channel status as of the last setup.
const CHANNELS = [
  ['email', 'Email', 'support@ for customers, wholesale@ for trade enquiries', 'cx-c-resolved', 'Connected'],
  ['ig', 'Instagram', '@fabvoguestudio direct messages', 'cx-c-resolved', 'Connected'],
  ['fb', 'Messenger', 'FAB VOGUE Studio page direct messages', 'cx-c-resolved', 'Connected'],
  ['form', 'Contact form', 'Website contact form, spam filtered', 'cx-c-resolved', 'Connected'],
  ['wa', 'WhatsApp', '+91 79901 05997, our own WhatsApp account (billed by Meta to our card). Free replies within 24 hours of the customer\'s last message', 'cx-c-resolved', 'Connected'],
];

// Email processing status: last run, emails waiting, last error
function EmailHealth({ api }) {
  const [h, setH] = useState(null);
  useEffect(() => { api('/api/v1/support/email-health').then((d) => setH(d.health)).catch(() => {}); }, [api]);
  if (!h) return null;
  const t = (ms) => (ms ? new Date(ms).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' }) : 'never');
  return (
    <div className={`cx-health-card${h.problem ? ' bad' : ''}`}>
      <b>{h.problem ? 'Email processing has a problem' : 'Email processing is working'}</b>
      {h.problem && <span>{h.problem}</span>}
      <span>Last successful check {t(h.lastOkAtMs)} · emails in the last check {h.waiting ?? 0}{h.errors ? ` · ${h.errors} failed` : ''}</span>
      {h.lastError && <span className="cx-hint">Last error ({t(h.lastErrorAtMs)}): {h.lastError}</span>}
    </div>
  );
}

function SettingsPage({ api, apiBase, getToken, isAdmin = true }) {
  const sections = isAdmin ? SECTIONS : SECTIONS.filter(([id]) => MANAGER_SECTIONS.has(id));
  const [stored, setSection] = useState(() => {
    try { return localStorage.getItem('cx-settings-section') || sections[0][0]; } catch { return sections[0][0]; }
  });
  const section = sections.some(([id]) => id === stored) ? stored : sections[0][0];
  const [members, setMembers] = useState(null);

  const pick = (id) => {
    setSection(id);
    try { localStorage.setItem('cx-settings-section', id); } catch { /* storage unavailable */ }
  };

  useEffect(() => {
    if (section === 'team' && members === null) api('/api/v1/support/team-members').then((d) => setMembers(d.members || [])).catch(() => setMembers([]));
  }, [api, section, members]);

  return (
    <div className="cx-page">
      <div className="cx-page-in">
        <div className="cx-page-head">
          <div>
            <h1>Settings</h1>
            <p className="cx-lede">{isAdmin ? 'Workspace settings apply to everyone on the support team. Only admins can change them.' : 'As Support manager you can change working hours, holidays and automatic replies here, and the chat flows on the WhatsApp page.'}</p>
          </div>
        </div>
        <div className="cx-settings">
          <nav className="cx-snav" aria-label="Settings sections">
            {sections.map(([id, label]) => <button key={id} aria-current={section === id} onClick={() => pick(id)}>{label}</button>)}
          </nav>
          <div style={{ minWidth: 0 }}>
            {section === 'appearance' && <InboxAppearance apiBase={apiBase} getToken={getToken} />}

            {section === 'team' && (
              <section className="cx-panel" aria-labelledby="cx-team-s">
                <div className="cx-panel-h"><h2 id="cx-team-s">Team</h2><span>{members ? `${members.length} with support access` : ''}</span></div>
                <div className="cx-panel-b">
                  {members === null ? <div className="cx-skel" style={{ height: 80, margin: 0 }} /> : members.length === 0 ? <span className="cx-hint">No one has support access yet.</span> : members.map((m) => (
                    <div key={m.uid} className="cx-need" style={{ paddingLeft: 0, paddingRight: 0 }}>
                      <span className="cx-avatar">{initials(m.displayName || m.email)}</span>
                      <div className="cx-need-main"><div><b style={{ fontWeight: 500 }}>{m.displayName || m.email}</b></div><div>{m.email}</div></div>
                    </div>
                  ))}
                </div>
                <div className="cx-panel-note">Who sees email, chats and carts follows each person's permissions in Admin, Users: View tickets, View live chats, View abandoned checkouts, AI agent tab, Archive tickets, View customers and View metrics.</div>
              </section>
            )}

            {section === 'channels' && (
              <section className="cx-panel" aria-labelledby="cx-ch-s">
                <div className="cx-panel-h"><h2 id="cx-ch-s">Channels</h2><span>every channel lands in one inbox</span></div>
                <div className="cx-panel-b">
                  <EmailHealth api={api} />
                  {CHANNELS.map(([ch, name, desc, tone, label]) => (
                    <div key={ch} className="cx-need" style={{ paddingLeft: 0, paddingRight: 0 }}>
                      <ChannelBadge channel={ch} large />
                      <div className="cx-need-main"><div><b style={{ fontWeight: 500 }}>{name}</b></div><div>{desc}</div></div>
                      <span className={`cx-chip ${tone}`}>{label}</span>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {section === 'agent' && (
              <>
                <section className="cx-panel" aria-labelledby="cx-ag-s">
                  <div className="cx-panel-h"><h2 id="cx-ag-s">AI agent</h2><AgentSwitch api={api} /></div>
                  <div className="cx-panel-b cx-hint" style={{ margin: 0 }}>When on, the agent drafts a reply for every new customer message on email, Instagram and Messenger. Every draft waits for a team member to approve, edit or reject it.</div>
                </section>
                <FactsPanel api={api} canEdit />
              </>
            )}

            {section === 'hours' && <AutoReplySettings api={api} />}
            {section === 'problems' && <ProblemsPage api={api} />}

            {section === 'email' && (
              <section className="cx-panel" aria-labelledby="cx-em-s">
                <div className="cx-panel-h"><h2 id="cx-em-s">Email and cart recovery</h2><span>filters, auto-reply, recovery emails</span></div>
                <div className="cx-panel-b cx-legacy">
                  <SupportSettings apiBase={apiBase} getToken={getToken} />
                </div>
              </section>
            )}

          </div>
        </div>
      </div>
    </div>
  );
}

export default SettingsPage;
